import InfiniteScroll from 'react-infinite-scroll-component';
import { Disclosure } from '@headlessui/react';
import { Role } from 'auth';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import '@/modules/common/styles/common.css';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import InvestorActivityFilters from '@/modules/investor-dashboard/components/InvestorActivityFilters.tsx';
import { InvestorDashboardWrapperOutletContext } from '@/modules/investor-dashboard/components/InvestorDashboardWrapper.tsx';
import InvestorTransactionItem from '@/modules/investor-dashboard/components/items/InvestorTransactionItem.tsx';
import { useMyActivitiesListQuery } from '@/modules/investor-dashboard/queries/activity.tsx';
import { EmptyListState, PageLayout, RoundBadge, Spinner } from 'ui';
import { FiltersIcon } from 'ui/icons';
import { ActivitiesStatus, MyActivitiesListV1SrActivitiesMyGetParams } from 'api';

type transactionFilters = Pick<
  MyActivitiesListV1SrActivitiesMyGetParams,
  | 'type'
  | 'status'
  | 'total_to'
  | 'register_class_id'
  | 'completion_date_from'
  | 'completion_date_to'
  | 'requested_at_date_from'
  | 'requested_at_date_to'
>;

const InvestorTransactionsHistory = () => {
  const [filters, setFilters] = useState<Partial<transactionFilters>>({});
  const { register_id } = useOutletContext<InvestorDashboardWrapperOutletContext>();

  const {
    data: transactionsData,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useMyActivitiesListQuery(
    {
      page: 1,
      page_size: 15,
      status: ActivitiesStatus.Completed,
      ...filters,
    },
    Number(register_id),
  );

  const transactions = (transactionsData?.pages || []).flatMap(({ data }) => data);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const appliedFiltersLength = Object.keys(filters).length;

  const headerAction = (
    <span>
      <Disclosure.Button>
        <div className="flex items-center border px-4 py-[0.5rem] rounded border-[rgba(23, 23, 23, 0.12)] gap-2">
          <img alt="filters" src={FiltersIcon} />
          <span className="text-sm">Filters</span>
          {!!appliedFiltersLength && <RoundBadge content={appliedFiltersLength} />}
        </div>
      </Disclosure.Button>
    </span>
  );

  return (
    <PageLayout
      title="Transactions history"
      HeaderWrapper={Disclosure}
      headerAction={headerAction}
      headerBottomContent={<InvestorActivityFilters filters={filters} setFilters={setFilters} />}
    >
      <EmptyListState
        title="No transactions yet"
        description="Your completed transactions and transfers will appear here"
        isLoading={isLoading}
        isSearchUsed={false}
        itemsCount={transactions.length}
      />
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={transactions.length}
        hasMore={hasNextPage || false}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        scrollableTarget="scrollable"
      >
        <div className="w-full flex flex-col space-y-2">
          {transactions.map(transaction => (
            <InvestorTransactionItem
              transaction={transaction}
              key={'InvestorTransactionItem_' + transaction.id}
            />
          ))}
        </div>
      </InfiniteScroll>
    </PageLayout>
  );
};

export default withPrivateAccess(InvestorTransactionsHistory, Role.SR_INVESTOR);
